import { useNavigate } from 'react-router-dom';
import { DataTable, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { ActionIcon } from '@repletoryreach/sdk-ui';
import { Menu } from '@repletoryreach/sdk-ui/overlays';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { TenantName } from '../../../components/TenantName';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { PersonAvatar } from './PersonAvatar';
import { useT } from '../../../i18n/I18nProvider';

type AdminUser = NonNullable<Awaited<ReturnType<typeof admin.listUsers>>['data']>[number];

export function UsersTable({ records, loading }: { records: AdminUser[]; loading: boolean }) {
  const navigate = useNavigate();
  const t = useT();
  const unnamed = t('users.unnamed');

  return (
    <DataTable<Indexed<AdminUser>>
      records={records as Indexed<AdminUser>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('users.emptyRows')}
      onRowClick={(row) => navigate(`/users/${row.id}`)}
      columns={[
        {
          accessor: 'display_name',
          title: t('users.columns.user'),
          sortable: true,
          // The design's user cell is a name over the address that identifies
          // it, which is also the pair an operator searches by.
          render: (r) => (
            <TwoLineCell
              primary={r.display_name || r.email || unnamed}
              secondary={r.email ?? t('users.noEmail')}
              title={r.email ?? undefined}
              leading={<PersonAvatar name={r.display_name || r.email || unnamed} />}
            />
          )
        },
        // The organisation is the axis a platform operator reads this table by,
        // and eight characters of its GUID was the one thing they could not read.
        { accessor: 'organisation_id', title: t('users.columns.organisation'), render: (r) => <TenantName id={r.organisation_id} /> },
        {
          accessor: 'scope',
          title: t('users.filters.scope'),
          sortable: true,
          render: (r) => (
            <StatusBadge size="xs" status={r.scope === 'platform' ? 'warning' : 'pending'}>
              {r.scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')}
            </StatusBadge>
          )
        },
        {
          accessor: 'created_at',
          title: t('users.columns.joined'),
          sortable: true,
          render: (r) => <TwoLineCell primary={formatDate(r.created_at)} secondary={formatRelative(r.created_at)} />
        },
        {
          accessor: 'actions',
          title: t('common.actions'),
          width: 56,
          render: (r) => <UserRowMenu user={r} />
        }
      ]}
    />
  );
}

function UserRowMenu({ user }: { user: AdminUser }) {
  const t = useT();
  const navigate = useNavigate();
  const name = user.display_name || user.email || t('users.unnamed');
  const unavailable = t('users.row.unavailable');

  return (
    // The row itself navigates, so the menu swallows its own clicks.
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Menu position="bottom-end" width={210}>
        <Menu.Target>
          <ActionIcon variant="subtle" size="sm" aria-label={t('users.row.menuLabel', { name })}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
              <circle cx="3" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="13" cy="8" r="1.4" />
            </svg>
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => navigate(`/users/${user.id}`)}>{t('users.row.view')}</Menu.Item>
          <Menu.Item onClick={() => navigate(`/tenants/${user.organisation_id}`)}>{t('users.row.openTenant')}</Menu.Item>
          <Menu.Divider />
          {/* Both of these need admin endpoints that have not shipped. They
              stay in place, and out of reach, rather than opening a confirm
              that resolves to a "coming soon" toast. */}
          <span title={unavailable}>
            <Menu.Item disabled>{t('users.row.reset')}</Menu.Item>
          </span>
          <span title={unavailable}>
            <Menu.Item disabled color="error">{t('users.row.disable')}</Menu.Item>
          </span>
        </Menu.Dropdown>
      </Menu>
    </span>
  );
}
